import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Box,
  Typography,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Button,
  CircularProgress,
  Chip,
  IconButton,
  Badge,
  Tabs,
  Tab,
  Stack,
  Alert,
  TextField,
  InputAdornment,
  Tooltip,
  MenuItem,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import LaunchIcon from '@mui/icons-material/Launch';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import api from '../../services/api';
import { format } from 'date-fns';
import { t } from '../../utils/textUtils';

const TYPE_LABELS = {
  progress_update: 'Progress Update',
  approval_request: 'Permintaan Approval',
  approval_response: 'Respon Approval',
  document_upload: 'Upload Dokumen',
  document_revision: 'Revisi Dokumen',
  issue_reported: 'Issue',
  deadline_reminder: 'Pengingat Deadline',
  milestone_update: 'Milestone',
  project_update: 'Update Proyek',
  budget: 'Budget',
  other: 'Lainnya',
};

const HIGH_PRIORITY_TYPES = new Set(['approval_request', 'deadline_reminder', 'issue_reported']);
const LOW_PRIORITY_TYPES = new Set(['document_upload', 'document_revision', 'milestone_update']);

const getPriority = (item) => {
  const explicit = String(item?.priority || '').toLowerCase();
  if (['high', 'medium', 'low'].includes(explicit)) return explicit;

  const type = String(item?.type || '').toLowerCase();
  if (HIGH_PRIORITY_TYPES.has(type)) return 'high';
  if (LOW_PRIORITY_TYPES.has(type)) return 'low';
  return 'medium';
};

const getPriorityColor = (priority) => {
  if (priority === 'high') return '#dc2626';
  if (priority === 'low') return '#16a34a';
  return '#f59e0b';
};

const getPriorityIcon = (priority) => {
  if (priority === 'high') return <PriorityHighIcon sx={{ color: '#dc2626', fontSize: 20 }} />;
  if (priority === 'low') return <InfoIcon sx={{ color: '#16a34a', fontSize: 20 }} />;
  return <WarningIcon sx={{ color: '#f59e0b', fontSize: 20 }} />;
};

const getTypeLabel = (type) => TYPE_LABELS[type] || type || 'Notifikasi';

const buildReadIdSet = () => {
  try {
    const stored = localStorage.getItem('notification_read_ids');
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
};

const formatTime = (createdAt) => {
  try {
    return format(new Date(createdAt), 'dd MMM yyyy HH:mm');
  } catch {
    return '-';
  }
};

const formatRelativeTime = (createdAt) => {
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return '-';

  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  if (diffMinutes < 1) return 'Baru saja';
  if (diffMinutes < 60) return `${diffMinutes} menit lalu`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} jam lalu`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays} hari lalu`;

  return formatTime(createdAt);
};

const Notifications = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(1);
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [, setReadIds] = useState(buildReadIdSet);

  const persistReadIds = useCallback((ids) => {
    try {
      localStorage.setItem('notification_read_ids', JSON.stringify(Array.from(ids)));
    } catch {
      // ignore write error
    }
  }, []);

  const buildStats = useCallback((items) => {
    const total = items.length;
    const unread = items.filter((item) => !item.isRead).length;
    const high = items.filter((item) => item.priority === 'high').length;
    return {
      total,
      unread,
      high,
      read: Math.max(total - unread, 0),
    };
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const currentReadIds = buildReadIdSet();
      setReadIds(currentReadIds);

      const response = await api.get('/notifications/events?limit=200');
      const rows = response.data?.notifications || [];
      const mapped = rows
        .map((item) => {
          const priority = getPriority(item);
          return {
            id: item.id,
            title: item.title || 'Notifikasi',
            message: item.message || '',
            type: item.type || 'other',
            category: item.category || '',
            createdAt: item.createdAt,
            source:
              item.projectName ||
              item.payload?.projectName ||
              item.Project?.name ||
              'Sistem',
            link: item.webLink || item.link || item.deepLink || null,
            priority,
            isRead: currentReadIds.has(item.id),
          };
        })
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setNotifications(mapped);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Gagal memuat notifikasi. Silakan coba lagi.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markAsRead = useCallback(
    (id) => {
      setNotifications((prev) =>
        prev.map((item) => (item.id === id ? { ...item, isRead: true } : item))
      );
      setReadIds((prev) => {
        const next = new Set(prev);
        next.add(id);
        persistReadIds(next);
        return next;
      });
    },
    [persistReadIds]
  );

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((item) => ({ ...item, isRead: true })));
    setReadIds((prev) => {
      const next = new Set(prev);
      notifications.forEach((item) => next.add(item.id));
      persistReadIds(next);
      return next;
    });
  }, [notifications, persistReadIds]);

  const handleNotificationClick = useCallback(
    (notification) => {
      if (!notification.isRead) {
        markAsRead(notification.id);
      }

      if (notification.link) {
        navigate(notification.link);
        return;
      }

      switch (notification.type) {
        case 'progress_update':
          navigate(user?.role === 'project_manager' ? '/progress/approval' : '/progress/submissions');
          break;
        case 'approval_request':
        case 'approval_response':
          navigate('/progress/approval');
          break;
        case 'document_upload':
        case 'document_revision':
          navigate('/documents');
          break;
        case 'deadline_reminder':
          navigate('/tasks');
          break;
        case 'budget':
          navigate('/budget');
          break;
        default:
          break;
      }
    },
    [markAsRead, navigate, user]
  );

  const stats = useMemo(() => buildStats(notifications), [buildStats, notifications]);

  const availableTypeFilters = useMemo(() => {
    const map = new Map();
    notifications.forEach((item) => {
      if (item.type && !map.has(item.type)) {
        map.set(item.type, getTypeLabel(item.type));
      }
    });
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [notifications]);

  const filteredNotifications = useMemo(() => {
    const q = query.trim().toLowerCase();

    return notifications.filter((item) => {
      if (tabValue === 1 && item.isRead) return false;
      if (tabValue === 2 && item.priority !== 'high') return false;
      if (tabValue === 3 && !item.isRead) return false;

      if (typeFilter !== 'all' && item.type !== typeFilter) return false;

      if (!q) return true;

      const searchable = [
        item.title,
        item.message,
        item.source,
        item.type,
        getTypeLabel(item.type),
      ]
        .join(' ')
        .toLowerCase();

      return searchable.includes(q);
    });
  }, [notifications, query, tabValue, typeFilter]);

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', p: { xs: 1.5, sm: 2.5, md: 3 }, bgcolor: '#F1F5F9' }}>
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: 2,
          borderRadius: 2,
          borderColor: '#dbeafe',
          background: 'linear-gradient(135deg, #f8fbff 0%, #eef4ff 100%)',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>
              {t('NOTIFIKASI')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
              {t('Pantau update proyek, approval, dan pengingat penting dalam satu panel.')}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
              <Chip size="small" label={`${stats.unread} BELUM DIBACA`} color={stats.unread > 0 ? 'error' : 'default'} sx={{ fontWeight: 700 }} />
              <Chip size="small" label={`${stats.high} PRIORITAS TINGGI`} color={stats.high > 0 ? 'warning' : 'default'} sx={{ fontWeight: 700 }} />
            </Stack>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchNotifications}>
              {t('SEGARKAN')}
            </Button>
            <Button
              variant="contained"
              onClick={markAllAsRead}
              disabled={stats.unread === 0}
              sx={{ bgcolor: '#0f172a', '&:hover': { bgcolor: '#0b1324' } }}
            >
              {t('TANDAI SEMUA DIBACA')}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider', bgcolor: '#fff' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  TOTAL
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>{stats.total}</Typography>
              </Box>
              <NotificationsIcon sx={{ fontSize: 34, color: '#1d4ed8' }} />
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider', bgcolor: '#fff' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  BELUM DIBACA
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#dc2626' }}>{stats.unread}</Typography>
              </Box>
              <Badge badgeContent={stats.unread} color="error">
                <NotificationsIcon sx={{ fontSize: 34, color: '#dc2626' }} />
              </Badge>
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider', bgcolor: '#fff' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  PRIORITAS TINGGI
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#f59e0b' }}>{stats.high}</Typography>
              </Box>
              <PriorityHighIcon sx={{ fontSize: 34, color: '#f59e0b' }} />
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider', bgcolor: '#fff' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  SUDAH DIBACA
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#16a34a' }}>{stats.read}</Typography>
              </Box>
              <CheckCircleIcon sx={{ fontSize: 34, color: '#16a34a' }} />
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2, borderColor: 'divider', bgcolor: '#fff' }}>
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 700 },
              '& .MuiTabs-indicator': { backgroundColor: '#0f172a', height: 3 },
            }}
          >
            <Tab label={`Semua (${stats.total})`} />
            <Tab label={`Belum dibaca (${stats.unread})`} />
            <Tab label={`Prioritas tinggi (${stats.high})`} />
            <Tab label={`Sudah dibaca (${stats.read})`} />
          </Tabs>

          <Grid container spacing={1.5}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                placeholder="Cari judul, pesan, sumber, atau tipe notifikasi..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Filter Tipe"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">Semua Tipe</MenuItem>
                {availableTypeFilters.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Stack>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredNotifications.length === 0 ? (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <NotificationsIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
              Tidak ada notifikasi
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Coba ubah tab atau kata kunci pencarian.
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {filteredNotifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                {index > 0 && <Divider component="li" />}
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemButton
                    alignItems="flex-start"
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      borderRadius: 1.5,
                      borderLeft: `4px solid ${getPriorityColor(notification.priority)}`,
                      backgroundColor: notification.isRead ? '#ffffff' : '#eff6ff',
                      '&:hover': { backgroundColor: notification.isRead ? '#f8fafc' : '#e8f1ff' },
                      py: 1.25,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 34, mt: 0.25 }}>
                      {getPriorityIcon(notification.priority)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: notification.isRead ? 600 : 800, color: '#0f172a' }}
                          >
                            {notification.title}
                          </Typography>
                          {!notification.isRead && (
                            <Chip size="small" label="BARU" color="primary" sx={{ height: 20, fontWeight: 700 }} />
                          )}
                          <Chip
                            size="small"
                            label={getTypeLabel(notification.type)}
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.72rem' }}
                          />
                          <Chip
                            size="small"
                            label={notification.priority.toUpperCase()}
                            sx={{
                              height: 20,
                              fontSize: '0.72rem',
                              fontWeight: 700,
                              color: '#fff',
                              bgcolor: getPriorityColor(notification.priority),
                            }}
                          />
                        </Stack>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.9 }}>
                            {notification.message || '-'}
                          </Typography>
                          <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" sx={{ flexWrap: 'wrap' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                              {notification.source}
                            </Typography>
                            <Tooltip title={formatTime(notification.createdAt)}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                {formatRelativeTime(notification.createdAt)}
                              </Typography>
                            </Tooltip>
                          </Stack>
                        </Box>
                      }
                    />

                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: 1.5 }}>
                      {notification.link && (
                        <Tooltip title="Buka Tujuan">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNotificationClick(notification);
                            }}
                          >
                            <LaunchIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!notification.isRead && (
                        <Tooltip title="Tandai Dibaca">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              markAsRead(notification.id);
                            }}
                            sx={{ color: '#0f172a' }}
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </ListItemButton>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
};

export default Notifications;
